import React, { useMemo } from "react";
import { Users } from "lucide-react";
import { FOLLOWER_TIERS, followerTier } from "./followerTiers";

const toneMap = {
  nano: ["bg-slate-50", "border-slate-200", "text-slate-500", "text-slate-900"],
  micro: ["bg-emerald-50", "border-emerald-200", "text-emerald-600", "text-emerald-700"],
  macro: ["bg-cyan-50", "border-cyan-200", "text-cyan-600", "text-cyan-700"],
  mega: ["bg-violet-50", "border-violet-200", "text-violet-600", "text-violet-700"],
  celebrity: ["bg-amber-50", "border-amber-200", "text-amber-600", "text-amber-700"],
};

/**
 * Creator-tier split card (Nano / Micro / Macro / Mega / Celebrity by
 * follower count). Toggleable from the Customize drawer (key "tier-split").
 * Computed from the stored summary — works on every existing report.
 */
export const CreatorTiers = ({ summary = [], visible = {} }) => {
  const counts = useMemo(() => {
    const c = {};
    for (const t of FOLLOWER_TIERS) c[t.id] = 0;
    for (const s of summary) c[followerTier(s.followers)]++;
    return c;
  }, [summary]);

  if (visible["tier-split"] === false) return null;
  if (!summary.length) return null;
  const total = summary.length;

  return (
    <div
      className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.03),0_4px_12px_-2px_rgba(15,23,42,0.04)]"
      data-testid="creator-tiers"
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5">
        <Users size={10} className="text-indigo-600" /> Creator split by followers
        <span className="text-slate-300 normal-case tracking-normal">
          — Nano &lt;10K · Micro 10K–100K · Macro 100K–1M · Mega 1M–10M · Celebrity 10M+
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        {FOLLOWER_TIERS.map((t) => {
          const [bg, border, labelC, valueC] = toneMap[t.id];
          const n = counts[t.id] || 0;
          const pct = total ? Math.round((n / total) * 100) : 0;
          return (
            <div
              key={t.id}
              className={`rounded-xl ${bg} border ${border} px-3 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.08)] transition-[transform,box-shadow] duration-200 ease-out`}
              title={t.hint}
              data-testid={`tier-tile-${t.id}`}
            >
              <div className={`text-[10px] font-mono uppercase tracking-wider ${labelC}`}>{t.label}</div>
              <div className={`text-base font-semibold ${valueC}`}>
                {n} / {total}
                <span className="ml-1 text-[10px] font-mono opacity-70">({pct}%)</span>
              </div>
              <div className="text-[9px] font-mono text-slate-400 mt-0.5">{t.hint}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
